import {Match, PersonFields} from "lib/types";
import {summarizeDaysAvailability, summarizePeriodsAvailability} from "lib/utils";
import {Avatar, Badge, Box, Button, Flex, rem, Text, Tooltip} from "@mantine/core";
import {IconMapPin} from "@tabler/icons-react";
import React from "react";

function generateRingfencedInfo(datetimeStr?: string) {
    if (!datetimeStr) {
        return 'Temporary match held for OP';
    }

    const now = new Date();
    const targetDate = new Date(datetimeStr);

    // @ts-ignore
    const diffTime = now - targetDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const formattedDate = targetDate.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    });

    const cleanedDate = formattedDate.replace(',', '');

    return `${diffDays} days  | ${cleanedDate}\nTemporary match held for OP`;
}

interface Props {
    pk: number | string,
    person: PersonFields,
    match?: Match
    personType: "older_person" | "volunteer";
    staticBaseUrl: string
}

export default function InfoCard({pk, person, match, personType, staticBaseUrl}: Props) {
    const isOP = personType === 'older_person'
    const matchType = match ? (match.score > 90 ? '#Excellent' : match.score > 50 ? '#Good' : '#Fair') : null;
    const matchTypeColor = match ? ( match.score > 90 ? '#249C4D' : match.score > 50 ? '#15A4DA' : '#E11B88' ) : '#fff';
    const availabilityDays = summarizeDaysAvailability(person.availability);
    const availabilityPeriods = summarizePeriodsAvailability(person.availability);

    return (
        <Flex
            direction={'column'}
            w={rem(304)}
            gap={rem(20)}
        >
            <Flex direction='row' gap={rem(20)}>

                {
                    !person.ringfenced ? (
                            <Avatar
                                color={'initials'}
                                name={person.name}
                                radius="xl"
                            />
                        ) :
                        (
                            <Tooltip
                                multiline
                                label={generateRingfencedInfo(person.ringfenced_date ?? undefined)}
                                w={rem(220)}
                            >
                                <Box pos={'relative'}>
                                    <Avatar
                                        color={'initials'}
                                        name={person.name}
                                        radius="xl"
                                        style={{border: person.ringfenced ? '2px solid #E1251B' : 'none'}}
                                    />

                                    <Box
                                        bg={'#E1251B'}
                                        style={{
                                            border: '2px solid #FFFFFF',
                                            borderRadius: '50%',
                                            width: rem(18),
                                            height: rem(18),
                                            position: 'absolute',
                                            top: rem(24),
                                            right: rem(-3),
                                            display: person.ringfenced ? 'block' : 'none'
                                        }}>
                                        <img src={`${staticBaseUrl}alone/images/ringfenced-icon.svg`} alt="Ringfenced"
                                             style={{
                                                 width: '9px',
                                                 height: '21px',
                                                 marginLeft: '2.5px',
                                                 marginTop: '-6px'
                                             }}/>
                                    </Box>
                                </Box>
                            </Tooltip>
                        )
                }

                <Flex direction='column' gap={rem(8)} flex={1}>
                    <Flex direction={'row'} justify={'space-between'} align={'center'}>
                        <Text
                            c={'#1A1D1F'}
                            fw={600}
                            fz={rem(15)}
                            lh={rem(24)}
                        >
                            {person.name}
                        </Text>

                        <Badge
                              c={isOP ? '#fff' : '#1A1D1F'}
                              bg={isOP ? '#4B49AC' : '#EFEFEF'}
                              fw={600}
                              fz={rem(15)}
                              lh={rem(24)}
                              tt={'capitalize'}
                              p={rem(8)}
                              radius="sm"
                              size={'lg'}
                          >{isOP ? 'OP' : 'VOL'}</Badge>
                    </Flex>

                    { match ? (
                        <Button
                            size={'sm'}
                            bg={'#FCFCFC'}
                            styles={{root: {borderColor: '#EFEFEF', borderWidth: '2px'}}}
                            radius={rem(8)}
                            py={rem(4)}
                            px={rem(8)}
                            component='a'
                            href={`/map/volunteer/${pk}`}
                            maw={rem(120)}
                            mah={rem(32)}
                        >
                            <Flex direction='row' align={'center'} gap={rem(4)}>
                                <IconMapPin color={'#1A1D1F'} stroke={2} size={20}/>
                                <Text
                                    c={'#1A1D1F'}
                                    fz={rem(12)}
                                    lh={rem(24)}
                                    fw={500}>
                                    <b>{match.travel_time__travel_minutes} min</b> { match.volunteer.fields.transport_type }
                                </Text>
                            </Flex>
                        </Button>
                    ) : (<></>)}

                    <Flex direction='row' justify={'space-between'} align={'center'}>
                        <Text
                            fw={600}
                            fz={rem(13)}
                            lh={rem(16)}
                            c={'#7C7F82'}
                            lineClamp={5}
                            maw={rem(200)}
                        >
                            {person.full_address}
                        </Text>
                    </Flex>

                    <Flex direction='row' gap={rem(12)}>
                        {/*Headers*/}
                        <Flex direction='column' gap={rem(4)}>
                            <Text c={'#272B30'} fz={rem(13)} lh={rem(16)} fw={600}>Age</Text>
                            <Text c={'#272B30'} fz={rem(13)} lh={rem(16)} fw={600}>Days</Text>
                            <Text c={'#272B30'} fz={rem(13)} lh={rem(16)} fw={600}>Time</Text>
                        </Flex>

                        {/*Values*/}
                        <Flex direction='column' gap={rem(4)}>
                            <Text c={'#6F767E'} fz={rem(13)} lh={rem(16)} fw={600}>{person.age}</Text>
                            <Text c={'#6F767E'} fz={rem(13)} lh={rem(16)} fw={600}>{availabilityDays}</Text>
                            <Text c={'#6F767E'} fz={rem(13)} lh={rem(16)} fw={600}>{availabilityPeriods}</Text>
                        </Flex>
                    </Flex>

                    <Flex direction={'row'} gap={rem(14)} align={'center'} mt={rem(8)}>
                        <Badge
                            c={person.days_waiting > 180 ? '#E1251B' : person.days_waiting > 90 ? '#F59E0B' : '#272B30'}
                            bg={person.days_waiting > 180 ? '#F9E6E5' : person.days_waiting > 90 ? '#FEF3C7' : '#EFEFEF'}
                            fw={600}
                            fz={rem(14)}
                            lh={rem(22)}
                            tt={'capitalize'}
                            p={rem(8)}
                            radius="sm"
                            size={'lg'}
                        >{person.days_waiting}</Badge>

                        <Badge
                            c={'#1A1D1F'}
                            bg={'#EFEFEF'}
                            fw={600}
                            fz={rem(14)}
                            lh={rem(22)}
                            tt={'capitalize'}
                            p={rem(8)}
                            radius="sm"
                            size={'lg'}
                        >{person.gender}</Badge>

                        { match ? (
                            <Badge
                                c={'#fff'}
                                bg={matchTypeColor}
                                fw={600}
                                fz={rem(14)}
                                lh={rem(22)}
                                tt={'capitalize'}
                                p={rem(8)}
                                radius="sm"
                                size={'lg'}
                            >{matchType}</Badge>
                        ) : (<></>)}
                    </Flex>
                </Flex>
            </Flex>
            <Button
                w={'100%'}
                size={'md'}
                bg={'#FCFCFC'}
                styles={{root: {borderColor: '#EFEFEF', borderWidth: '2px'}}}
                radius={rem(8)}
                component='a'
                href={`https://bconnectplatform.lightning.force.com/lightning/r/Contact/${person.salesforce_id}/view`}
                target="_blank"
                rel="noopener noreferrer"
            >
                <Text
                    c={'#1A1D1F'}
                    fz={rem(14)}
                    lh={rem(24)}
                    fw={600}
                >
                    Contact Info
                </Text>
            </Button>
        </Flex>
    )
}